import React from 'react'
import {
  Button,
  Heading,
  Text,
  Input,
  Select,
} from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { useAuthContext } from '../../contexts/AuthContext'
import { comercialAtividadesService } from '../../services/comercialAtividades'
import { userLogsService } from '../../services/userLogs'
import type { ComercialLookupItem } from '../../types/comercialCadastro'
import type {
  ActivityDestinationKind,
  ComercialActivity,
  ComercialActivityInput,
  OverallFeedback,
} from '../../types/comercialAtividades'
import {
  FEEDBACK_BADGE_CLASS,
  FEEDBACK_LABELS,
} from '../../types/comercialAtividades'
import { BrokerCombobox } from '../Qualidade/BrokerCombobox'
import { EyeIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

const MODULE_NAME = 'comercial_atividades'
const COMERCIAL_ATIVIDADES_LOG_ORIGEM = 'comercial/atividades'
const COMERCIAL_ATIVIDADES_LOG_INFO = 'Módulo Comercial • Atividades • Registro'

function resolveFormLabels(
  data: ComercialActivityInput,
  broker: FormModalState['broker'],
  activityTypes: ComercialLookupItem[],
  visitObjectives: ComercialLookupItem[],
) {
  const brokerLabel = broker
    ? `${broker.name}${broker.id_scgp != null ? ` (${broker.id_scgp})` : ''}`
    : data.broker_id ?? undefined
  return {
    brokerLabel,
    activityTypeName: activityTypes.find((t) => t.id === data.activity_type_id)?.name,
    visitObjectiveName: visitObjectives.find((o) => o.id === data.visit_objective_id)?.name,
  }
}

function formInputSnapshotForLog(
  data: ComercialActivityInput,
  context: {
    brokerLabel?: string
    activityTypeName?: string
    visitObjectiveName?: string
    responsibleName?: string
  },
) {
  return {
    activity_date: data.activity_date,
    broker_id: data.broker_id,
    broker_label: context.brokerLabel,
    visited_location: data.visited_location?.trim() || null,
    activity_type_id: data.activity_type_id,
    activity_type_name: context.activityTypeName,
    visit_objective_id: data.visit_objective_id,
    visit_objective_name: context.visitObjectiveName,
    overall_feedback: data.overall_feedback,
    overall_feedback_label: FEEDBACK_LABELS[data.overall_feedback],
    general_perceptions: data.general_perceptions.trim(),
    commercial_responsible_name: context.responsibleName,
  }
}

function activitySnapshotForLog(activity: ComercialActivity) {
  return {
    id: activity.id,
    activity_date: activity.activity_date.slice(0, 10),
    broker_id: activity.broker_id,
    broker_label: formatBrokerLabel(activity),
    visited_location: activity.visited_location?.trim() || null,
    activity_type_id: activity.activity_type_id,
    activity_type_name: activity.activity_type?.name,
    visit_objective_id: activity.visit_objective_id,
    visit_objective_name: activity.visit_objective?.name,
    overall_feedback: activity.overall_feedback,
    overall_feedback_label: FEEDBACK_LABELS[activity.overall_feedback],
    general_perceptions: activity.general_perceptions,
    commercial_responsible_name: activity.commercial_responsible_name,
  }
}

function logSubjectFromInput(
  data: ComercialActivityInput,
  brokerLabel?: string,
): string {
  if (data.broker_id) return brokerLabel || data.broker_id
  return data.visited_location?.trim() || '—'
}

function logSubjectFromActivity(activity: ComercialActivity): string {
  return formatLocalVisitado(activity)
}

async function logAtividadeCreate(
  data: ComercialActivityInput,
  context: {
    brokerLabel?: string
    activityTypeName?: string
    visitObjectiveName?: string
    responsibleName?: string
  },
): Promise<void> {
  const snapshot = formInputSnapshotForLog(data, context)
  const subject = logSubjectFromInput(data, context.brokerLabel)
  try {
    await userLogsService.logCreate(
      'other',
      null,
      `Registrou atividade comercial - ${formatDate(data.activity_date)} - ${subject}`,
      {
        origem: COMERCIAL_ATIVIDADES_LOG_ORIGEM,
        additional_info: COMERCIAL_ATIVIDADES_LOG_INFO,
        new_values: snapshot,
      },
    )
  } catch (logError) {
    console.warn('Erro ao registrar log (atividades — incluir):', logError)
  }
}

async function logAtividadeUpdate(
  activityId: string,
  previous: ComercialActivity,
  data: ComercialActivityInput,
  context: {
    brokerLabel?: string
    activityTypeName?: string
    visitObjectiveName?: string
  },
): Promise<void> {
  const oldSnapshot = activitySnapshotForLog(previous)
  const newSnapshot = formInputSnapshotForLog(data, {
    ...context,
    responsibleName: previous.commercial_responsible_name,
  })
  const subject = logSubjectFromInput(data, context.brokerLabel)
  try {
    await userLogsService.logUpdate(
      'other',
      activityId,
      `Editou atividade comercial - ${formatDate(data.activity_date)} - ${subject}`,
      oldSnapshot,
      newSnapshot,
      {
        origem: COMERCIAL_ATIVIDADES_LOG_ORIGEM,
        additional_info: COMERCIAL_ATIVIDADES_LOG_INFO,
        activity_id: activityId,
      },
    )
  } catch (logError) {
    console.warn('Erro ao registrar log (atividades — editar):', logError)
  }
}

async function logAtividadeDelete(activity: ComercialActivity): Promise<void> {
  const snapshot = activitySnapshotForLog(activity)
  try {
    await userLogsService.logDelete(
      'other',
      activity.id,
      `Excluiu atividade comercial - ${formatDate(activity.activity_date)} - ${logSubjectFromActivity(activity)}`,
      {
        origem: COMERCIAL_ATIVIDADES_LOG_ORIGEM,
        additional_info: COMERCIAL_ATIVIDADES_LOG_INFO,
        deleted_activity: snapshot,
      },
    )
  } catch (logError) {
    console.warn('Erro ao registrar log (atividades — excluir):', logError)
  }
}

const EMPTY_FORM: ComercialActivityInput = {
  activity_date: '',
  broker_id: null,
  visited_location: null,
  activity_type_id: '',
  visit_objective_id: '',
  overall_feedback: 'neutro',
  general_perceptions: '',
}

interface FormModalState {
  mode: 'create' | 'edit'
  activityId?: string
  data: ComercialActivityInput
  destinationKind: ActivityDestinationKind
  broker?: { name: string; id_scgp: number | null } | null
}

interface AtividadesRegistroTabProps {
  activities: ComercialActivity[]
  totalCount: number
  onDataChange: () => Promise<void>
  canViewValues: boolean
  activityTypes: ComercialLookupItem[]
  visitObjectives: ComercialLookupItem[]
  lookupsLoading: boolean
}

function formatDate(value: string): string {
  if (!value) return '—'
  const [year, month, day] = value.slice(0, 10).split('-')
  if (year && month && day) return `${day}/${month}/${year}`
  return new Date(value).toLocaleDateString('pt-BR')
}

function formatBrokerLabel(activity: ComercialActivity): string {
  if (!activity.broker?.name) return '—'
  const scgp = activity.broker.id_scgp != null ? ` (${activity.broker.id_scgp})` : ''
  return `${activity.broker.name}${scgp}`
}

function formatVisitedLocation(activity: ComercialActivity): string {
  const value = activity.visited_location?.trim()
  return value ? value : '—'
}

/** Corretor | Outro — indica a origem do valor na coluna Identificação. */
function formatDestinationKindLabel(activity: ComercialActivity): 'Corretor' | 'Outro' {
  return activity.broker_id ? 'Corretor' : 'Outro'
}

/** Valor unificado: corretor se houver, senão local visitado. */
function formatLocalVisitado(activity: ComercialActivity): string {
  if (activity.broker_id) {
    const brokerLabel = formatBrokerLabel(activity)
    return brokerLabel !== '—' ? brokerLabel : '—'
  }
  return formatVisitedLocation(activity)
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  const maybe = error as { message?: string; code?: string }
  if (maybe?.message) return maybe.message
  if (maybe?.code) return `Erro (${maybe.code})`
  return 'Erro inesperado ao processar solicitação.'
}

function activityToForm(activity: ComercialActivity): FormModalState {
  const hasBroker = Boolean(activity.broker_id)
  return {
    mode: 'edit',
    activityId: activity.id,
    destinationKind: hasBroker ? 'broker' : 'custom',
    data: {
      activity_date: activity.activity_date.slice(0, 10),
      broker_id: activity.broker_id,
      visited_location: activity.visited_location ?? null,
      activity_type_id: activity.activity_type_id,
      visit_objective_id: activity.visit_objective_id,
      overall_feedback: activity.overall_feedback,
      general_perceptions: activity.general_perceptions ?? '',
    },
    broker: activity.broker
      ? { name: activity.broker.name, id_scgp: activity.broker.id_scgp }
      : null,
  }
}

const AtividadesRegistroTab: React.FC<AtividadesRegistroTabProps> = ({
  activities,
  totalCount,
  onDataChange,
  canViewValues,
  activityTypes,
  visitObjectives,
  lookupsLoading,
}) => {
  const { profile } = useAuthContext()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate, loading: loadingUpdatePerm } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete, loading: loadingDeletePerm } = usePermission(MODULE_NAME, 'delete')
  const permissionsLoading = loadingUpdatePerm || loadingDeletePerm

  const canEditActivity = React.useCallback(
    (activity: ComercialActivity): boolean => {
      if (!canUpdate) return false
      if (canViewValues) return true
      return profile?.id != null && activity.commercial_responsible_user_id === profile.id
    },
    [canUpdate, canViewValues, profile?.id],
  )

  const canDeleteActivity = React.useCallback(
    (activity: ComercialActivity): boolean => {
      if (!canDelete) return false
      if (canViewValues) return true
      return profile?.id != null && activity.commercial_responsible_user_id === profile.id
    },
    [canDelete, canViewValues, profile?.id],
  )

  const [formModal, setFormModal] = React.useState<FormModalState | null>(null)
  const [submittingForm, setSubmittingForm] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)

  const [detailActivity, setDetailActivity] = React.useState<ComercialActivity | null>(null)

  const [deleteModal, setDeleteModal] = React.useState<{
    id: string
    label: string
  } | null>(null)
  const [submittingDelete, setSubmittingDelete] = React.useState(false)
  const [tabError, setTabError] = React.useState<string | null>(null)
  const [currentPage, setCurrentPage] = React.useState(1)
  const [itemsPerPage] = React.useState(20)

  React.useEffect(() => {
    setCurrentPage(1)
  }, [activities])

  const totalPages = Math.max(1, Math.ceil(activities.length / itemsPerPage))
  const pageRows = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return activities.slice(start, start + itemsPerPage)
  }, [activities, currentPage, itemsPerPage])

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const openNew = () => {
    setFormError(null)
    const today = new Date().toISOString().slice(0, 10)
    setFormModal({
      mode: 'create',
      destinationKind: 'broker',
      data: { ...EMPTY_FORM, activity_date: today },
      broker: null,
    })
  }

  const openEdit = (activity: ComercialActivity) => {
    if (!canEditActivity(activity)) return
    setFormError(null)
    setFormModal(activityToForm(activity))
  }

  const closeFormModal = () => {
    if (submittingForm) return
    setFormModal(null)
    setFormError(null)
  }

  const validateForm = (
    data: ComercialActivityInput,
    destinationKind: ActivityDestinationKind,
  ): string | null => {
    if (!data.activity_date) return 'A data da atividade é obrigatória.'
    if (destinationKind === 'broker') {
      if (!data.broker_id) return 'O corretor é obrigatório.'
    } else if (!data.visited_location?.trim()) {
      return 'A identificação é obrigatória.'
    }
    if (!data.activity_type_id) return 'O tipo de atividade é obrigatório.'
    if (!data.visit_objective_id) return 'O objetivo da visita é obrigatório.'
    if (!data.overall_feedback) return 'O feedback geral é obrigatório.'
    return null
  }

  const handleFormSave = async () => {
    if (!formModal) return
    const validationError = validateForm(formModal.data, formModal.destinationKind)
    if (validationError) {
      setFormError(validationError)
      return
    }

    const payload: ComercialActivityInput = {
      ...formModal.data,
      broker_id: formModal.destinationKind === 'broker' ? formModal.data.broker_id : null,
      visited_location:
        formModal.destinationKind === 'custom'
          ? formModal.data.visited_location?.trim() || null
          : null,
    }

    if (formModal.mode === 'create' && !canInsert) return
    if (formModal.mode === 'edit') {
      if (!canUpdate) return
      const editingActivity = activities.find((a) => a.id === formModal.activityId)
      if (!editingActivity || !canEditActivity(editingActivity)) {
        setFormError('Você não tem permissão para editar esta atividade.')
        return
      }
    }

    if (formModal.mode === 'create' && (!profile?.id || !profile.name)) {
      setFormError('Não foi possível identificar o usuário logado.')
      return
    }

    setSubmittingForm(true)
    setFormError(null)
    try {
      const formLabels = resolveFormLabels(
        payload,
        formModal.destinationKind === 'broker' ? formModal.broker : null,
        activityTypes,
        visitObjectives,
      )

      if (formModal.mode === 'create') {
        await comercialAtividadesService.createActivity(payload, {
          userId: profile!.id,
          name: profile!.name ?? 'Usuário',
        })
        await logAtividadeCreate(payload, {
          ...formLabels,
          responsibleName: profile!.name ?? 'Usuário',
        })
      } else if (formModal.activityId) {
        const editingActivity = activities.find((a) => a.id === formModal.activityId)
        await comercialAtividadesService.updateActivity(formModal.activityId, payload)
        if (editingActivity) {
          await logAtividadeUpdate(formModal.activityId, editingActivity, payload, formLabels)
        }
      }
      closeFormModal()
      await onDataChange()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSubmittingForm(false)
    }
  }

  const openDeleteModal = (activity: ComercialActivity) => {
    if (!canDeleteActivity(activity)) return
    setDeleteModal({
      id: activity.id,
      label: `${formatDate(activity.activity_date)} - ${logSubjectFromActivity(activity)}`,
    })
  }

  const closeDeleteModal = () => {
    if (submittingDelete) return
    setDeleteModal(null)
  }

  const confirmDelete = async () => {
    if (!deleteModal) return
    const activity = activities.find((a) => a.id === deleteModal.id)
    if (!activity || !canDeleteActivity(activity)) {
      setTabError('Você não tem permissão para excluir esta atividade.')
      closeDeleteModal()
      return
    }
    setSubmittingDelete(true)
    setTabError(null)
    try {
      await comercialAtividadesService.softDeleteActivity(deleteModal.id)
      await logAtividadeDelete(activity)
      closeDeleteModal()
      await onDataChange()
    } catch (error) {
      setTabError(getErrorMessage(error))
    } finally {
      setSubmittingDelete(false)
    }
  }

  return (
    <div className="p-6 space-y-4">
      {tabError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{tabError}</Text>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
                Registro de Atividades
              </Heading>
              <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                {activities.length}
                {activities.length !== totalCount ? ` de ${totalCount}` : ''}{' '}
                {activities.length === 1 ? 'registro' : 'registros'}
              </span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {canInsert && (
                <Button variant="solid" size="md" onClick={openNew} disabled={lookupsLoading}>
                  <PlusIcon className="h-4 w-4" />
                  Registrar atividade
                </Button>
              )}
            </div>
          </div>
        </div>

        {activities.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Text size="sm" color="muted">
              {totalCount === 0
                ? 'Nenhuma atividade registrada.'
                : 'Nenhum resultado para os filtros aplicados.'}
            </Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Destino
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Identificação
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Objetivo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Feedback
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Responsável
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {pageRows.map((activity) => (
                  <tr key={activity.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {formatDate(activity.activity_date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDestinationKindLabel(activity)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {formatLocalVisitado(activity)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {activity.activity_type?.name ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {activity.visit_objective?.name ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${FEEDBACK_BADGE_CLASS[activity.overall_feedback]}`}
                      >
                        {FEEDBACK_LABELS[activity.overall_feedback]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {activity.commercial_responsible_name}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          title="Ver detalhes"
                          onClick={() => setDetailActivity(activity)}
                          className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {!permissionsLoading && canEditActivity(activity) && (
                          <button
                            type="button"
                            title="Editar"
                            onClick={() => openEdit(activity)}
                            className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {!permissionsLoading && canDeleteActivity(activity) && (
                          <button
                            type="button"
                            title="Excluir"
                            onClick={() => openDeleteModal(activity)}
                            className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <Text size="sm" color="muted">
                Exibindo {pageRows.length} de {activities.length}{' '}
                {activities.length === 1 ? 'registro filtrado' : 'registros filtrados'}.
              </Text>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <Text size="sm" color="muted">
                  Página {currentPage} de {totalPages}
                </Text>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {formModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
                {formModal.mode === 'create' ? 'Registrar atividade' : 'Editar atividade'}
              </Text>
              <Button variant="ghost" onClick={closeFormModal} disabled={submittingForm}>
                Fechar
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Data da atividade <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={formModal.data.activity_date}
                  onChange={(e) =>
                    setFormModal((prev) =>
                      prev ? { ...prev, data: { ...prev.data, activity_date: e.target.value } } : prev
                    )
                  }
                />
              </div>

              <div>
                <label
                  id="atividade-destino-label"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Destino da visita <span className="text-red-500">*</span>
                </label>
                <div
                  className="flex flex-wrap gap-4 sm:gap-6 mb-3"
                  role="radiogroup"
                  aria-labelledby="atividade-destino-label"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="atividade-destino"
                      value="broker"
                      checked={formModal.destinationKind === 'broker'}
                      onChange={() =>
                        setFormModal((prev) =>
                          prev
                            ? {
                                ...prev,
                                destinationKind: 'broker',
                                data: { ...prev.data, visited_location: null },
                              }
                            : prev
                        )
                      }
                      className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-800 dark:text-gray-200 font-medium">
                      Corretor cadastrado
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="atividade-destino"
                      value="custom"
                      checked={formModal.destinationKind === 'custom'}
                      onChange={() =>
                        setFormModal((prev) =>
                          prev
                            ? {
                                ...prev,
                                destinationKind: 'custom',
                                data: { ...prev.data, broker_id: null },
                                broker: null,
                              }
                            : prev
                        )
                      }
                      className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-800 dark:text-gray-200 font-medium">
                      Identificação
                    </span>
                  </label>
                </div>

                {formModal.destinationKind === 'broker' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Corretor <span className="text-red-500">*</span>
                    </label>
                    <BrokerCombobox
                      value={formModal.data.broker_id || null}
                      initialBroker={formModal.broker ?? null}
                      onChange={(id, broker) =>
                        setFormModal((prev) =>
                          prev
                            ? {
                                ...prev,
                                data: {
                                  ...prev.data,
                                  broker_id: id ?? null,
                                  visited_location: null,
                                },
                                broker: broker
                                  ? { name: broker.name, id_scgp: broker.id_scgp }
                                  : null,
                              }
                            : prev
                        )
                      }
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Identificação <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      placeholder="Nome ou identificação..."
                      value={formModal.data.visited_location ?? ''}
                      onChange={(e) =>
                        setFormModal((prev) =>
                          prev
                            ? {
                                ...prev,
                                data: {
                                  ...prev.data,
                                  visited_location: e.target.value,
                                  broker_id: null,
                                },
                                broker: null,
                              }
                            : prev
                        )
                      }
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo de atividade <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formModal.data.activity_type_id}
                  onChange={(e) =>
                    setFormModal((prev) =>
                      prev ? { ...prev, data: { ...prev.data, activity_type_id: e.target.value } } : prev
                    )
                  }
                >
                  <option value="">Selecione...</option>
                  {activityTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Objetivo da visita <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formModal.data.visit_objective_id}
                  onChange={(e) =>
                    setFormModal((prev) =>
                      prev ? { ...prev, data: { ...prev.data, visit_objective_id: e.target.value } } : prev
                    )
                  }
                >
                  <option value="">Selecione...</option>
                  {visitObjectives.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Feedback geral <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formModal.data.overall_feedback}
                  onChange={(e) =>
                    setFormModal((prev) =>
                      prev
                        ? {
                            ...prev,
                            data: {
                              ...prev.data,
                              overall_feedback: e.target.value as OverallFeedback,
                            },
                          }
                        : prev
                    )
                  }
                >
                  <option value="positivo">{FEEDBACK_LABELS.positivo}</option>
                  <option value="neutro">{FEEDBACK_LABELS.neutro}</option>
                  <option value="negativo">{FEEDBACK_LABELS.negativo}</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Percepções gerais
                </label>
                <textarea
                  value={formModal.data.general_perceptions}
                  onChange={(e) =>
                    setFormModal((prev) =>
                      prev
                        ? { ...prev, data: { ...prev.data, general_perceptions: e.target.value } }
                        : prev
                    )
                  }
                  rows={4}
                  placeholder="Descreva as percepções gerais da visita..."
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Responsável comercial
                </label>
                <Input
                  type="text"
                  value={
                    formModal.mode === 'edit'
                      ? activities.find((a) => a.id === formModal.activityId)?.commercial_responsible_name ?? '—'
                      : profile?.name ?? '—'
                  }
                  disabled
                />
              </div>
            </div>

            {formError && (
              <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <Text size="sm">{formError}</Text>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="ghost" onClick={closeFormModal} disabled={submittingForm}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={handleFormSave} disabled={submittingForm}>
                {submittingForm ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {detailActivity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
                Detalhes da atividade
              </Text>
              <Button variant="ghost" onClick={() => setDetailActivity(null)}>
                Fechar
              </Button>
            </div>

            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Data</dt>
                <dd className="text-gray-900 dark:text-white font-medium">{formatDate(detailActivity.activity_date)}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Destino</dt>
                <dd className="text-gray-900 dark:text-white font-medium">{formatDestinationKindLabel(detailActivity)}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Identificação</dt>
                <dd className="text-gray-900 dark:text-white font-medium">{formatLocalVisitado(detailActivity)}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Tipo de atividade</dt>
                <dd className="text-gray-900 dark:text-white font-medium">{detailActivity.activity_type?.name ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Objetivo da visita</dt>
                <dd className="text-gray-900 dark:text-white font-medium">{detailActivity.visit_objective?.name ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Feedback geral</dt>
                <dd>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${FEEDBACK_BADGE_CLASS[detailActivity.overall_feedback]}`}
                  >
                    {FEEDBACK_LABELS[detailActivity.overall_feedback]}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Percepções gerais</dt>
                <dd className="text-gray-900 dark:text-white whitespace-pre-wrap">
                  {detailActivity.general_perceptions?.trim() || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Responsável comercial</dt>
                <dd className="text-gray-900 dark:text-white font-medium">{detailActivity.commercial_responsible_name}</dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Confirmar Exclusão
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Você está excluindo: <strong className="text-gray-900 dark:text-white">{deleteModal.label}</strong>
              <br />
              O registro não será removido do banco de dados (exclusão lógica).
            </Text>

            <div className="flex justify-end gap-3 mt-4">
              <Button variant="ghost" onClick={closeDeleteModal} disabled={submittingDelete}>
                Cancelar
              </Button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={submittingDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {submittingDelete ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AtividadesRegistroTab
